import axios from "axios";
import {API_URL} from "@/service/config";
import authHeader from "@/service/auth-header";
import { refreshAccessToken } from "@/service/authSession";


const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 10000,
    headers: {
        "Content-Type": "application/json",
    }
})

apiClient.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        const originalRequest = error.config;

        if (
            !error.response
            || error.response.status !== 401
            || !originalRequest
            || originalRequest._retry
            || originalRequest.skipAuthRefresh
        ) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
            const refreshResult = await refreshAccessToken();

            if (refreshResult.status !== 'success') {
                return Promise.reject(error);
            }

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;
            return apiClient(originalRequest);
        } catch (refreshError) {
            return Promise.reject(refreshError);
        }
    }
);


export const Api = {

    async createEmptyWorkflow() {
        let response = await apiClient.post(
            "/workflows",
            {},
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )
        return response.data.workflow_id;
    },
    async getWorkflows(page=1) {
        const workflows = await apiClient.get(
            "/workflows?page=" + page,
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )
        return workflows.data
    },
    async getWorkflow(workflow_id) {
        const workflow = await apiClient.get(
            `/workflows/${workflow_id}`,
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )
        return workflow.data
    },
    async createUserWorkflow() {
        const workflow = await apiClient.post(
            API_URL + "/workflows",
            {},
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )

        return workflow.data
    },
    async getGoogleAccount() {
        const google_account = await apiClient.get(
            API_URL + "/google/account",
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )
            
        return {
            "email": google_account.data.email
        }
    },
    async getUserGoogleAccessToken() {
        const response = await apiClient.get(
            API_URL + "/user/google/account",
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )

        return response.data
    },
    async getGoogleDriveFiles(
        fileType,
        sharedWithMe,
        starredFiles,
        orderBy,
        desc,
        nextPageToken,
        folderId,
        searchString,
    ) {
        let googleDriveFiles = {}
        try {
            googleDriveFiles = await apiClient.get(
                API_URL + "/google/drive/files?" +
                "file_type=" + fileType +
                "&shared_with_me=" + sharedWithMe +
                "&starred_files=" + starredFiles +
                "&order_by=" + orderBy +
                "&desc=" + desc +
                "&next_page_token=" + nextPageToken +
                "&folder_id=" + folderId +
                "&search_string=" + searchString,
                {
                    headers: {
                        'Authorization': authHeader()
                    }
                }
            )
        } catch (e) {
            if (!e.response){
                return
            }
            if (e.response.status === 403) {
                return {
                    "error": 403
                }
            }
        }


        return googleDriveFiles.data

    },
    async getSpreadSheetTabs(spreadsheetId) {
        const spreadsheetTabs = await apiClient.get(
            API_URL + "/google/drive/" + spreadsheetId + "/tabs",
            {
                headers: {
                    'Authorization': authHeader()
                }
            }
        )

        return spreadsheetTabs.data

    },
    async attachConnectionToWorkflow(workflow_id, source, target, _filter, include_header, append) {
        await apiClient.post(
            API_URL + "/workflows/" + workflow_id + "/connections",
            {
                "file_source": {
                    "spreadsheet_id": source.file.id,
                    "tab_id": source.tab.sheetId.toString(),
                    "file_type": "GOOGLE_SHEET"
                },
                "file_target": {
                    "spreadsheet_id": target.file.id.toString(),
                    "tab_id": target.tab.sheetId.toString(),
                },
                "filter": _filter,
                "append": append,
                "include_header": include_header
            },
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
    },
    async getUserInfo() {
        let response = {}
        try {
            response = await apiClient.get(
                API_URL + "/auth/user/code/info",
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                }
            )
        } catch(e) {
            if (!e.response) {
                return {
                    status: "error",
                    message: "Что-то пошло не так"
                }
            }
            if (e.response.status === 403) {
                return {
                    status: "error",
                    message: "Что-то пошло не так",
                    code: 403
                }
            }
        }
        return {
            status: "success",
            data: response.data,
        }
    },
    async getUserMeInfo() {
        let response = await apiClient.get(
            API_URL + "/auth/user",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getUserIntegrationsReport() {
        let response = await apiClient.get(
            API_URL + "/auth/user/integrations/report",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getUserPaymentsHistory(limit = null) {
        const params = limit != null ? { limit } : {};
        let response = await apiClient.get(
            API_URL + "/auth/user/payments/history",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params,
            }
        )
        return response.data
    },
    async isUserConfirmed() {
        let response = await apiClient.get(
            API_URL + "/auth/user/is_confirmed",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async validateUserCode(userCode) {
        let response = {}
        try {
           response = await apiClient.post(
                API_URL + "/auth/register/validate_registration_code?registration_code=" + userCode,
                {},
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                },
            )
        }
        catch(e) {
            let message = ""
            if (e.response.status === 405) {
                message = "Неверный код"
            }
            else if (e.response.status === 408) {
                message = "Пользователь уже подтверждён"
            }
            else if (e.response.status === 409) {
                message = "Код регистрации истёк"
            }
            else {
                message = "Неизвестная ошибка"
            }
            return {
                "is_valid": false,
                "message": message
            }
        }

        return response.data
    },
    async resendUserCode() {
        let response = await apiClient.post(
            API_URL + "/auth/code/resend",
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async addToFavorite(workflow_id) {
        let response = await apiClient.post(
            API_URL + "/workflows/" + workflow_id + "/favorite",
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async removeFromFavorite(workflow_id) {
        let response = await apiClient.delete(
            API_URL + "/workflows/" + workflow_id + "/favorite",
            {
                headers: { "Authorization": authHeader()} 
            }
        )
        return response.data
    },
    async getGoogleOauthUrl() {
        let response = await apiClient.get(
            API_URL + "/google/oauth",
            {
                headers: { "Authorization": authHeader() }
            }
        )
        return response.data.redirect_uri
    },
    googleCallback(state, code, scope, authUser, _prompt) {
        return apiClient.get(
            API_URL + 
            "/callback/google?" + 
            "&state=" + state +
            "&code=" + code +
            "&scope=" + scope +
            "&authUser=" + authUser +
            "&prompt=" + _prompt,
            {
                headers: { "Authorization": authHeader() }
            }
        )
    },
    async startWorkflow(workflow_id) {
        let response = await apiClient.post(
            API_URL + "/workflows/" + workflow_id + "/start",
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getWorkflowConnections(workflow_id) {
        let response = await apiClient.get(
            API_URL + "/workflows/" + workflow_id + "/connections",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    }, 
    async getWorkflowFiles(workflow_id) {
        let response = await apiClient.get(
            API_URL + "/workflows/" + workflow_id + "/files",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async startConnection(workflow_id, connection_id) {
        let response = await apiClient.post(
            API_URL + "/workflows/" + workflow_id + "/connections/" + connection_id + "/start",
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async updateConnectionsOrder(workflow_id, connections) {
        let response = await apiClient.post(
            API_URL + "/workflows/" + workflow_id + "/connections/order",
            {
                "connections": connections
            },
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getPaymentUrl(subscription, duration, userInfo) {
        let response = await apiClient.post(
            API_URL + "/subscriptions/purchase",
            {
                "subscription_plan_id": subscription.id,
                "duration_id": duration.id,
                "full_name": userInfo.fullName,
                "email": userInfo.email,
            },
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getTransactionsPaymentUrl(transactions, userInfo) {
        let response = await apiClient.post(
            API_URL + "/subscriptions/transactions/purchase",
            {
                "transactions": transactions,
                "full_name": userInfo.fullName,
                "email": userInfo.email,
            },
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getSubscriptionPlans() {
        let response = await apiClient.get(
            API_URL + "/subscriptions",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getSubscriptionDurations() {
        let response = await apiClient.get(
            API_URL + "/subscriptions/durations",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async GoogleLogOut() {
        let response = await apiClient.post(
            API_URL + "/google/logout",
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getTimeZonesInfo() {
        let response = await apiClient.get(
            API_URL + "/timezones",
            {}
        )
        return response.data
    },
    async getWorkflowSettings(workflow_id) {
        let response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/settings`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async saveWorkflowSettings(workflow_id, settings) {
        let response = await apiClient.post(
            API_URL + `/workflows/${workflow_id}/settings`,
            settings,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async updateFilesPositions(workflow_id, files) {
        let response = await apiClient.post(
            API_URL + `/workflows/${workflow_id}/connections/positions`,
            {"files": files},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getConnectionsStatuses(workflow_id) {
        let response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/connections/statuses`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getConnectionStatus(workflow_id, connection_id) {
        let response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/connections/${connection_id}/statuses`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async sendRequestOnEmailChange(email) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/auth/email/request?email=${email}`,
                {},
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                }
            )

        } catch (e) {
            if (e.response) {
                if (e.response.status === 400) {
                    return {
                        status: "error",
                        message: e.response.data.detail
                    }
                }
                if (e.response.status === 401) {
                    return {
                        status: "error",
                        message: e.response.data.detail
                    }
                }
                if (e.response.status === 422) {
                    return {
                        status: "error",
                        message: "Введите действующую почту"
                    }
                }

            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async changeEmail(email, code) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/auth/email?email=${email}&code=${code}`,
                {},
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                }
            )
        } catch (e) {
            if (e.response && e.response.status === 400) {
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async resendEmailRequestCode(email) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/auth/email/code/resend?email=${email}`,
                {},
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                }
            )
        } catch (e) {
            if (e.response && e.response.status === 400) {
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async resetPasswordRequest(email) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/auth/password/reset/request`,
                {
                    "email": email
                },
            )
        } catch (e) {
            if (e.response && e.response.status === 400) {
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async resetPassword(reset_hash, new_password) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/auth/password/reset`,
                {
                    "reset_hash": reset_hash,
                    "new_password": new_password
                },
            )
        } catch (e) {
            if (e.response && (e.response.status === 400) || (e.response.status === 422)) {
                if (e.response.status === 422) {return {status: "error", message: "Пароль не может быть пустым"}}
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async getTableColumns(spreadsheetId, tabId, headerIndex) {
        let response = {}
        try {
            response = await apiClient.get(
                API_URL + `/google/drive/${spreadsheetId}/sheet/${tabId}/columns?header_index=${headerIndex}`,
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                },
            )
        } catch (e) {
            if (e.response && (e.response.status === 400) || (e.response.status === 422)) {
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async getTableColumnsEmpty() {
        let response = {}
        response = await apiClient.get(
            API_URL + `/google/drive/sheet/columns/empty`,
            {},
        )
        return {
            status: "success",
            data: response.data
        }
    },
    async getFilterCriteria() {
        const response = await apiClient.get(
            API_URL + `/workflows/filter/criteria`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async getFlowObjects(workflow_id) {
        const response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/flow`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async getConnection(workflow_id, connection_id) {
        const response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/connections/${connection_id}`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async updateConnection(workflow_id, connection_id, connection_for_update) {
        const response = await apiClient.post(
            API_URL + `/workflows/${workflow_id}/connections/${connection_id}`,
            connection_for_update,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async updateWorkflowName(workflow_id, name) {
        const response = await apiClient.post(
            API_URL + `/workflows/${workflow_id}/name?name=${name}`,
            {},
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async createConnection(workflow_id, requestBody) {
        let response = {}
        try {
            response = await apiClient.post(
                API_URL + `/workflows/${workflow_id}/connections`,
                requestBody,
                {
                    headers: {
                        "Authorization": authHeader()
                    }
                },
            )
        } catch (e) {
            if (e.response && e.response.status === 400) {
                return {
                    status: "error",
                    message: e.response.data.detail
                }
            }
        }
        return {
            status: "success",
            data: response.data
        }
    },
    async deleteConnection(workflow_id, connection_id) {
        const response = await apiClient.delete(
            API_URL + `/workflows/${workflow_id}/connections/${connection_id}`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
    async getWorkflowTransactions(workflow_id) {
        const response = await apiClient.get(
            API_URL + `/workflows/${workflow_id}/transactions/history`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async getOrderPaymentById(orderId) {
        const response = await apiClient.get(
            API_URL + `/payment/info?order_id=${orderId}`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async YandexOauthLogin(hashParams) {
        const response = await apiClient.post(
            API_URL + `/auth/yandex/oauth`,
            hashParams,
            {
                withCredentials: true
            }
        )
        let access_token = response.data.access_token

        localStorage.setItem("accessToken", access_token)
        localStorage.setItem("tokenType", response.data.token_type)
        localStorage.setItem("expiresAt", response.data.expires_at)

        return {
            status: "success",
        }
    },
    async AttachYandexAccount(hashParams) {
        const response = await apiClient.post(
            API_URL + `/auth/yandex/account/attach`,
            hashParams,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return {
            response: response,
            status: "success",
        }
    },
    async VKOauthLogin(vk_oauth) {
        const response = await apiClient.post(
            API_URL + `/auth/vk/oauth`,
            vk_oauth,
            {
                withCredentials: true
            }
        )
        let access_token = response.data.access_token

        localStorage.setItem("accessToken", access_token)
        localStorage.setItem("tokenType", response.data.token_type)
        localStorage.setItem("expiresAt", response.data.expires_at)

        return {
            status: "success",
        }
    },
    async getUserYandexAcc() {
        const response = await apiClient.get(
            API_URL + `/auth/yandex/accounts`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            },
        )
        return response.data
    },
    async removeYandexAccount(yandex_account_id) {
        const response = await apiClient.delete(
            API_URL + `/auth/yandex/account?yandex_account_id=${yandex_account_id}`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return {
            response: response,
            status: "success",
        }
    },
    async deleteWorkFlow(workflowId) {
        let response = await apiClient.delete(
            API_URL + "/workflows/" + workflowId,
            {
                headers: { "Authorization": authHeader()}
            }
        )
        return response.data
    },
    async attachVkAcc(code) {
        let response = await apiClient.post(
            API_URL + `/auth/vk/ads/accounts?code=${code}`,
            {},
            {
                headers: { "Authorization": authHeader()}
            }
        )
        return response.data
    },
    async getVkAccounts() {
        let response = await apiClient.get(
            API_URL + `/auth/vk/ads/accounts`,
            {
                headers: { "Authorization": authHeader()}
            }
        )
        return response.data
    },
    async deleteVkAcc(acc_id) {
        let response = await apiClient.delete(
            API_URL + `/auth/vk/ads/accounts?account_id=${acc_id}`,
            {
                headers: { "Authorization": authHeader()}
            }
        )
        return response.data
    },
    async getYandexFiles(yandexAccountId, externalLink) {
        const response = await apiClient.get(
            API_URL + "/yandex/files",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    yandex_account: yandexAccountId,
                    external_link: externalLink
                },
                timeout: 60000
            }
        )
        return response.data
    },

    async getBitrixDealDirections(domain) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/deals/directions",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                }
            }
        )
        return response.data
    },

    async getBitrixDealStages(domain, directionId) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/deals/stages",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                    direction_id: directionId,
                }
            }
        )
        return response.data
    },

    async getBitrixLeadStages(domain) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/leads/stages",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                }
            }
        )
        return response.data
    },

    async getBitrixDealFields(domain) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/deals/fields",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                }
            }
        )
        return response.data
    },

    async getBitrixLeadFields(domain) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/leads/fields",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                }
            }
        )
        return response.data
    },

    async getBitrixOAuthUrl(domain) {
        const response = await apiClient.get(
            API_URL + "/bitrix24/oauth",
            {
                headers: {
                    "Authorization": authHeader()
                },
                params: {
                    domain,
                }
            }
        )
        return response.data.redirect_uri
    },

    async completeBitrixOAuth(payload) {
        const response = await apiClient.post(
            API_URL + "/bitrix24/oauth/callback",
            payload,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },

    async getUserBitrixAccounts() {
        const response = await apiClient.get(
            API_URL + "/bitrix24/accounts",
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },

    async removeBitrixAccount(installationId) {
        const response = await apiClient.delete(
            API_URL + `/bitrix24/accounts/${installationId}`,
            {
                headers: {
                    "Authorization": authHeader()
                }
            }
        )
        return response.data
    },
}
